import React from 'react';

const nodeTypes = [
  {
    type: 'rectangle',
    label: 'Process',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="6" width="18" height="12" rx="2" strokeWidth="2" />
      </svg>
    )
  },
  {
    type: 'rounded',
    label: 'Terminal',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <rect x="3" y="6" width="18" height="12" rx="6" strokeWidth="2" />
      </svg>
    )
  },
  {
    type: 'diamond',
    label: 'Decision',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M12 3 L21 12 L12 21 L3 12 Z" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    type: 'parallelogram',
    label: 'I/O',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M7 6 L21 6 L17 18 L3 18 Z" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    )
  },
  {
    type: 'text',
    label: 'Text',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M4 7V4h16v3M9 20h6M12 4v16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }
];

const Toolbar = ({
  selectedNodeId,
  selectedNodeIds = [],
  connectingFrom,
  canUndo,
  canRedo,
  clipboard = { nodes: [] },
  onAddNode,
  onAddSwimlane,
  onConnect,
  onCancelConnect,
  onDelete,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onAutoLayout,
  onAlign,
  onDistribute,
  onBulkEdit,
  onCreateGroup
}) => {
  const hasSelection = selectedNodeIds.length > 0;
  const hasClipboard = clipboard.nodes.length > 0;

  return (
    <div className="h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 flex items-center gap-2">
      {/* Node types */}
      <div className="flex items-center gap-1 pr-3 border-r border-gray-200 dark:border-gray-700">
        <span className="text-xs text-gray-500 dark:text-gray-400 mr-2 font-medium">Add Node:</span>
        {nodeTypes.map((nodeType) => (
          <button
            key={nodeType.type}
            onClick={() => onAddNode(nodeType.type)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            title={`Add ${nodeType.label}`}
          >
            {nodeType.icon}
            <span className="text-sm hidden sm:inline">{nodeType.label}</span>
          </button>
        ))}
      </div>

      {/* Swimlane button */}
      <div className="flex items-center gap-1 pr-3 border-r border-gray-200 dark:border-gray-700">
        <button
          onClick={onAddSwimlane}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 transition-colors"
          title="Add Swimlane (vertical column for organizing flows)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          <span className="text-sm hidden sm:inline">Add Lane</span>
        </button>
      </div>

      {/* Connection button */}
      <div className="flex items-center gap-1 px-3 border-r border-gray-200 dark:border-gray-700">
        {connectingFrom ? (
          <button
            onClick={onCancelConnect}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="text-sm">Cancel</span>
          </button>
        ) : (
          <button
            onClick={() => selectedNodeId && onConnect(selectedNodeId)}
            disabled={!selectedNodeId}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
              selectedNodeId
                ? 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
            }`}
            title={selectedNodeId ? 'Connect selected node' : 'Select a node first'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className="text-sm hidden sm:inline">Connect</span>
          </button>
        )}
      </div>

      {/* Edit actions */}
      <div className="flex items-center gap-1 px-3 border-r border-gray-200 dark:border-gray-700">
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className={`p-2 rounded-lg transition-colors ${
            canUndo
              ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
          title="Undo (Ctrl+Z)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className={`p-2 rounded-lg transition-colors ${
            canRedo
              ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
          title="Redo (Ctrl+Shift+Z)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
          </svg>
        </button>
      </div>

      {/* Clipboard actions */}
      <div className="flex items-center gap-1 px-3 border-r border-gray-200 dark:border-gray-700">
        <button
          onClick={onCopy}
          disabled={!hasSelection}
          className={`p-2 rounded-lg transition-colors ${
            hasSelection
              ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
          title={hasSelection ? `Copy ${selectedNodeIds.length} node(s) (Ctrl+C)` : 'Select nodes to copy'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>
        <button
          onClick={onPaste}
          disabled={!hasClipboard}
          className={`p-2 rounded-lg transition-colors ${
            hasClipboard
              ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
          title={hasClipboard ? `Paste ${clipboard.nodes.length} node(s) (Ctrl+V)` : 'Nothing to paste'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </button>
      </div>

      {/* Node actions (when selected) */}
      {hasSelection && (
        <div className="flex items-center gap-1 px-3 border-r border-gray-200 dark:border-gray-700">
          {/* Create Group button */}
          <button
            onClick={onCreateGroup}
            className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 transition-colors"
            title={`Group ${selectedNodeIds.length} node(s)`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </button>
          {/* Bulk Edit button (only when multiple nodes selected) */}
          {selectedNodeIds.length >= 2 && (
            <button
              onClick={onBulkEdit}
              className="p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400 transition-colors"
              title={`Edit ${selectedNodeIds.length} nodes at once`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
            title={`Delete ${selectedNodeIds.length} node(s) (Delete)`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      )}

      {/* Layout actions */}
      <div className="flex items-center gap-1 px-3 border-r border-gray-200 dark:border-gray-700">
        <button
          onClick={() => onAutoLayout?.('TB')}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
          title="Auto-layout (Top to Bottom)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16M4 10h16M4 15h16M4 20h16" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v4m0 10v4M8 7l4-4 4 4M8 17l4 4 4-4" />
          </svg>
          <span className="text-sm hidden xl:inline">Auto Layout</span>
        </button>

        {/* Alignment buttons (only show when multiple nodes selected) */}
        {selectedNodeIds.length >= 2 && (
          <>
            <div className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-1" />
            <button
              onClick={() => onAlign?.('left')}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
              title="Align left"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v16M8 8h12M8 16h8" />
              </svg>
            </button>
            <button
              onClick={() => onAlign?.('center')}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
              title="Align center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M6 8h12M8 16h8" />
              </svg>
            </button>
            <button
              onClick={() => onAlign?.('top')}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
              title="Align top"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16M8 8v12M16 8v8" />
              </svg>
            </button>
            <button
              onClick={() => onDistribute?.('horizontal')}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
              title="Distribute horizontally"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M8 8v8M12 6v12M16 8v8" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Keyboard shortcuts hint */}
      <div className="hidden lg:flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
        <span>Shift+Click: Multi-select</span>
        <span>•</span>
        <span>Ctrl+A: Select all</span>
        <span>•</span>
        <span>Double-click: Edit</span>
      </div>
    </div>
  );
};

export default Toolbar;
